import React, { useState, useEffect } from 'react';
import {
  Stack,
  Text,
  PrimaryButton,
  DefaultButton,
  SearchBox,
  Dropdown,
  IDropdownOption,
  DetailsList,
  IColumn,
  Selection,
  SelectionMode,
  CommandBar,
  ICommandBarItemProps,
  MessageBar,
  MessageBarType,
  Modal,
  Dialog,
  DialogType,
  DialogFooter
} from '@fluentui/react';
import {
  AddRegular,
  EditRegular,
  DeleteRegular,
  NewsRegular,
  StarRegular,
  StarOffRegular,
  EyeRegular,
  CalendarRegular
} from '@fluentui/react-icons';
import styles from './NewsManagement.module.css';
import { authenticatedFetch } from '../../utils/authApi';
import NewsEditor from './NewsEditor';

interface NewsArticle {
  id: string;
  title: string;
  summary: string;
  content: string;
  category: string;
  author: string;
  tags: string[];
  is_featured: boolean;
  publish_date: string;
  view_count: number;
  likes: number;
  status: string;
  additional_images?: string[];
}

const NewsManagement: React.FC = () => {
  const [articles, setArticles] = useState<NewsArticle[]>([]);
  const [filteredArticles, setFilteredArticles] = useState<NewsArticle[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Editor state
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState<NewsArticle | null>(null);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  
  // Selection and dialog
  const [selection] = useState(new Selection());
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [articleToDelete, setArticleToDelete] = useState<NewsArticle | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    filterArticles();
  }, [articles, searchTerm, selectedCategory, statusFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [articlesResponse, categoriesResponse] = await Promise.all([
        authenticatedFetch('/api/news?limit=100'),
        authenticatedFetch('/api/news/categories')
      ]);

      const articlesData = await articlesResponse.json();
      const categoriesData = await categoriesResponse.json();

      if (articlesData.success) {
        setArticles(articlesData.articles);
      }

      if (categoriesData.success) {
        setCategories(['all', ...categoriesData.categories]);
      }

    } catch (error) {
      console.error('Failed to load data:', error);
      setError('Failed to load news data');
    } finally {
      setLoading(false);
    }
  };

  const filterArticles = () => {
    let filtered = [...articles];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(article =>
        article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        article.summary.toLowerCase().includes(searchTerm.toLowerCase()) ||
        article.author.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Category filter
    if (selectedCategory && selectedCategory !== 'all') {
      filtered = filtered.filter(article => article.category === selectedCategory);
    }

    // Status filter
    if (statusFilter !== 'all') {
      if (statusFilter === 'featured') {
        filtered = filtered.filter(article => article.is_featured);
      } else {
        filtered = filtered.filter(article => article.status === statusFilter);
      }
    }

    setFilteredArticles(filtered);
  };

  const handleCreateArticle = () => {
    setEditingArticle(null);
    setIsEditorOpen(true);
  };

  const handleEditArticle = (article: NewsArticle) => {
    setEditingArticle(article);
    setIsEditorOpen(true);
  };

  const handleDeleteClick = (article: NewsArticle) => {
    setArticleToDelete(article);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!articleToDelete) return;

    try {
      const response = await authenticatedFetch(`/api/news/${articleToDelete.id}`, {
        method: 'DELETE'
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Article deleted successfully');
        loadData();
      } else {
        setError(data.error || 'Failed to delete article');
      }
    } catch (error) {
      setError('Failed to delete article');
    } finally {
      setDeleteDialogOpen(false);
      setArticleToDelete(null);
    }
  };

  const handleToggleFeatured = async (article: NewsArticle) => {
    try {
      const response = await authenticatedFetch(`/api/news/${article.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          is_featured: !article.is_featured
        })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(`Article ${article.is_featured ? 'removed from' : 'added to'} featured`);
        loadData();
      } else {
        setError(data.error || 'Failed to update article');
      }
    } catch (error) {
      setError('Failed to update article');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const columns: IColumn[] = [
    {
      key: 'title',
      name: 'Title',
      fieldName: 'title',
      minWidth: 200,
      maxWidth: 300,
      isResizable: true,
      onRender: (item: NewsArticle) => (
        <div className={styles.titleCell}>
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
            {item.is_featured && <StarRegular className={styles.featuredIcon} />}
            <Text variant="medium" className={styles.titleText}>
              {item.title}
            </Text>
          </Stack>
          <Text variant="small" className={styles.summaryText}>
            {item.summary.substring(0, 100)}...
          </Text>
        </div>
      )
    },
    {
      key: 'category',
      name: 'Category',
      fieldName: 'category',
      minWidth: 100,
      maxWidth: 150,
      isResizable: true,
      onRender: (item: NewsArticle) => (
        <div className={styles.categoryBadge}>
          {item.category}
        </div>
      )
    },
    {
      key: 'author',
      name: 'Author',
      fieldName: 'author',
      minWidth: 120,
      maxWidth: 180,
      isResizable: true
    },
    {
      key: 'publish_date',
      name: 'Published',
      fieldName: 'publish_date',
      minWidth: 140,
      maxWidth: 180,
      isResizable: true,
      onRender: (item: NewsArticle) => (
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 6 }}>
          <CalendarRegular className={styles.dateIcon} />
          <Text variant="small">{formatDate(item.publish_date)}</Text>
        </Stack>
      )
    },
    {
      key: 'stats',
      name: 'Stats',
      minWidth: 100,
      maxWidth: 120,
      isResizable: true,
      onRender: (item: NewsArticle) => (
        <Stack tokens={{ childrenGap: 4 }}>
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 6 }}>
            <EyeRegular className={styles.statIcon} />
            <Text variant="small">{item.view_count}</Text>
          </Stack>
          <Text variant="small" className={styles.likesText}>
            ♥ {item.likes}
          </Text>
        </Stack>
      )
    },
    {
      key: 'actions',
      name: 'Actions',
      minWidth: 120,
      maxWidth: 120,
      onRender: (item: NewsArticle) => (
        <Stack horizontal tokens={{ childrenGap: 8 }}>
          <DefaultButton
            iconProps={{ iconName: 'Edit' }}
            onClick={() => handleEditArticle(item)}
            className={styles.actionButton}
            title="Edit article"
          />
          <DefaultButton
            iconProps={{ iconName: item.is_featured ? 'FavoriteStarFill' : 'FavoriteStar' }}
            onClick={() => handleToggleFeatured(item)}
            className={`${styles.actionButton} ${item.is_featured ? styles.featuredButton : ''}`}
            title={item.is_featured ? 'Remove from featured' : 'Add to featured'}
          />
          <DefaultButton
            iconProps={{ iconName: 'Delete' }}
            onClick={() => handleDeleteClick(item)}
            className={`${styles.actionButton} ${styles.deleteButton}`}
            title="Delete article"
          />
        </Stack>
      )
    }
  ];

  const commandBarItems: ICommandBarItemProps[] = [
    {
      key: 'new',
      text: 'New Article',
      iconProps: { iconName: 'Add' },
      onClick: handleCreateArticle
    },
    {
      key: 'refresh',
      text: 'Refresh',
      iconProps: { iconName: 'Refresh' },
      onClick: loadData
    }
  ];

  const categoryOptions: IDropdownOption[] = categories.map(cat => ({
    key: cat,
    text: cat === 'all' ? 'All Categories' : cat.charAt(0).toUpperCase() + cat.slice(1)
  }));

  const statusOptions: IDropdownOption[] = [
    { key: 'all', text: 'All Articles' },
    { key: 'published', text: 'Published' },
    { key: 'featured', text: 'Featured Only' },
    { key: 'draft', text: 'Drafts' }
  ];

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
          <Stack tokens={{ childrenGap: 8 }}>
            <Stack horizontal tokens={{ childrenGap: 12 }} verticalAlign="center">
              <NewsRegular className={styles.headerIcon} />
              <Text variant="xxLarge" className={styles.headerTitle}>
                News Management
              </Text>
            </Stack>
            <Text variant="mediumPlus" className={styles.headerSubtitle}>
              Create and manage news articles and announcements
            </Text>
          </Stack>
        </Stack>
      </div>

      {/* Messages */}
      {error && (
        <MessageBar
          messageBarType={MessageBarType.error}
          onDismiss={() => setError(null)}
          className={styles.messageBar}
        >
          {error}
        </MessageBar>
      )}

      {success && (
        <MessageBar
          messageBarType={MessageBarType.success}
          onDismiss={() => setSuccess(null)}
          className={styles.messageBar}
        >
          {success}
        </MessageBar>
      )}

      {/* Filters */}
      <div className={styles.filtersSection}>
        <Stack horizontal tokens={{ childrenGap: 16 }} wrap>
          <SearchBox
            placeholder="Search articles..."
            value={searchTerm}
            onChange={(_, value) => setSearchTerm(value || '')}
            className={styles.searchBox}
          />
          <Dropdown
            placeholder="Category"
            options={categoryOptions}
            selectedKey={selectedCategory}
            onChange={(_, option) => setSelectedCategory(option?.key as string)}
            className={styles.filterDropdown}
          />
          <Dropdown
            placeholder="Status"
            options={statusOptions}
            selectedKey={statusFilter}
            onChange={(_, option) => setStatusFilter(option?.key as string)}
            className={styles.filterDropdown}
          />
        </Stack>
      </div>

      {/* Command Bar */}
      <CommandBar items={commandBarItems} className={styles.commandBar} />

      {/* Articles List */}
      <div className={styles.listContainer}>
        <DetailsList
          items={filteredArticles}
          columns={columns}
          selection={selection}
          selectionMode={SelectionMode.none}
          layoutMode={1}
          isHeaderVisible={true}
          className={styles.articlesList}
        />
      </div>

      {/* Stats Summary */}
      <div className={styles.statsSection}>
        <Stack horizontal tokens={{ childrenGap: 32 }}>
          <div className={styles.statCard}>
            <Text variant="large" className={styles.statNumber}>
              {articles.length}
            </Text>
            <Text variant="medium">Total Articles</Text>
          </div>
          <div className={styles.statCard}>
            <Text variant="large" className={styles.statNumber}>
              {articles.filter(a => a.is_featured).length}
            </Text>
            <Text variant="medium">Featured</Text>
          </div>
          <div className={styles.statCard}>
            <Text variant="large" className={styles.statNumber}>
              {categories.length - 1}
            </Text>
            <Text variant="medium">Categories</Text>
          </div>
          <div className={styles.statCard}>
            <Text variant="large" className={styles.statNumber}>
              {articles.reduce((sum, a) => sum + a.view_count, 0)}
            </Text>
            <Text variant="medium">Total Views</Text>
          </div>
        </Stack>
      </div>

      {/* News Editor */}
      <NewsEditor
        isOpen={isEditorOpen}
        onDismiss={() => setIsEditorOpen(false)}
        article={editingArticle as any}
        onSave={() => {
          loadData();
          setIsEditorOpen(false);
        }}
      />

      {/* Delete Dialog */}
      <Dialog
        hidden={!deleteDialogOpen}
        onDismiss={() => setDeleteDialogOpen(false)}
        dialogContentProps={{
          type: DialogType.normal,
          title: 'Delete Article',
          subText: `Are you sure you want to delete "${articleToDelete?.title}"? This action cannot be undone.`
        }}
      >
        <DialogFooter>
          <PrimaryButton
            onClick={handleDeleteConfirm}
            text="Delete"
            iconProps={{ iconName: 'Delete' }}
          />
          <DefaultButton
            onClick={() => setDeleteDialogOpen(false)}
            text="Cancel"
          />
        </DialogFooter>
      </Dialog>
    </div>
  );
};

export default NewsManagement;
